import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps,
} from '@mui/material';
import { useId } from 'react';
import { COLORS } from '../../constants';

/**
 * Renders a custom select component with a custom label and placeholder, extends MUI SelectProps.
 *
 * @param {string} label - The label of the Dropdown component.
 * @param {string} placeholder - The placeholder of the Dropdown component.
 * @param {string} errorText - The helper text to show when an error exists.
 * @param {Array<Object>} options - The options for the Dropdown component.
 * @param {boolean} hide - Whether to hide the Dropdown component.
 * @return {React.JSX.Element} The rendered Dropdown component.
 */
export const Dropdown = ({
  label,
  placeholder,
  options,
  helperText,
  hide = false,
  ...selectProps
}: {
  label: string;
  placeholder?: string;
  helperText?: string;
  options: { label: string; value: string }[];
  hide?: boolean;
} & SelectProps) => {
  const { sx, value, required, error, inputProps, ...rest } = selectProps;
  const labelId = useId();
  const helperId = useId();
  const showHelper = Boolean(required && helperText && error);
  return (
    <FormControl error={error} fullWidth sx={{ display: hide ? 'none' : 'block' }}>
      <InputLabel
        id={labelId}
        required={required}
        sx={{ color: rest.disabled ? COLORS.text.placeholder : COLORS.text.label }}
      >
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        displayEmpty
        value={value}
        required={required}
        error={error}
        disabled={rest.disabled}
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                maxHeight: '30vh',
                overflow: 'auto',
              },
            },
          },
        }}
        sx={value === '' ? { color: COLORS.text.placeholder, ...sx } : sx}
        {...rest}
        inputProps={{ 'aria-describedby': showHelper ? helperId : undefined, ...inputProps }}
      >
        {placeholder && (
          <MenuItem value="" disabled sx={{ display: 'none' }}>
            {placeholder}
          </MenuItem>
        )}
        {options.map(({ label: _label, value: _value }, index) => (
          <MenuItem key={index} value={_value}>
            {rest.multiple ? (
              <>
                <Checkbox checked={Array.isArray(value) && value.includes(_value)} />
                <ListItemText primary={_label} />
              </>
            ) : (
              _label
            )}
          </MenuItem>
        ))}
      </Select>
      {showHelper && (
        <FormHelperText id={helperId} sx={{ margin: '3px 14px 0px' }} error={error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
